import isValidDate from 'date-fns/isValid';
import conversions from '@sstdev/lib_epc-conversions';
import fileImport from '../../../FORMS/importNamespace/importRelation/fileImport';
import { constants, metadata } from 'lib_ui-services';
import { processFailedRecords } from './doingCreate_import_import';
import { offerFailedRecordsDownload } from './didCreate_import_import';

const _p = { loadFile: fileImport.getData, offerFailedRecordsDownload };
export const _private = _p;

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

export default {
    verb: 'willCreate',
    namespace: 'import',
    relation: 'import',
    priority: 20, // before the default willCreate
    description: 'Load and Validate the data that is to be imported',
    logic: willCreate,
    onError
};

/**
 * @param {{
 *      error: Error;
 *      data: T;
 *      context: Context;
 *      dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>,
 *      workflowStack: WorkflowStack[]
 * }} parameters
 * */
function onError({ data, error, dispatch }) {
    const { newRecord } = data || {};
    const { foreignNamespace, foreignRelation } = newRecord || {};
    closeImportForm(dispatch, foreignNamespace, foreignRelation);
    throw error;
}

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function willCreate({ data, log, dispatch }) {
    const { newRecord } = data || { newRecord: {} };
    const {
        _id,
        title,
        firstRowContainsHeader,
        foreignNamespace,
        foreignRelation,
        columns,
        file: [file] = []
    } = newRecord;

    if (!_id) return;

    const startedAt = new Date();
    const rows = await _p.loadFile(file);
    const normalizedColumns = normalizeImportColumns(columns);
    const parsedData = parseImportData(
        rows,
        normalizedColumns,
        firstRowContainsHeader,
        log,
        foreignNamespace,
        foreignRelation
    );

    closeImportForm(dispatch, foreignNamespace, foreignRelation);
    validateImportData(parsedData, { title, columns: normalizedColumns }, log, dispatch);

    return {
        ...data,
        newRecord: {
            _id,
            title,
            firstRowContainsHeader,
            foreignNamespace,
            foreignRelation,
            columns: normalizedColumns,
            fileName: file.name,
            fileSize: file.size,
            startedAt,
            data: parsedData
        }
    };
}

function parseImportData(rows, columns, firstRowContainsHeader, log, foreignNamespace, foreignRelation) {
    return rows
        .map(transformRowToObject(columns, firstRowContainsHeader, log, foreignNamespace, foreignRelation))
        .filter(x => !!x);
}

/** On a row that was valid but not imported because the import was aborted. */
export const NOT_IMPORTED_ABORTED_MESSAGE = 'Not imported: the import was aborted';

/** A row as it goes into the aborted import's download: its own errors, or why it was not imported. */
function withAbortReason(row) {
    return row.errors?.length ? row : { ...row, errors: [NOT_IMPORTED_ABORTED_MESSAGE] };
}

function validateImportData(parsedData, { title, columns }, log, dispatch) {
    if (parsedData.slice(0, 100).every(d => d.errors?.length)) {
        log.error(`Import "${title}" failed: No valid data in first 100 rows.`);
        const message = `Import "${title}" failed: No valid data in first 100 rows. Import aborted.`;

        dispatch(
            {
                message,
                timeout: 5000,
                addToList: true,
                isError: true, //todo: gl2025 remove
                variant: 'error',
                type: constants.notificationTypes.TOAST
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
        // The toast alone said only that the import was aborted. Offer the failed records too, as a
        // finished import does, so the operator can see why each row failed and fix the file
        // (#2282). EVERY row, the valid ones included: nothing was imported, and a download holding
        // only the rows with errors, fixed and re-imported, would create an order without its
        // valid lines — the partial order the order import exists to prevent.
        _p.offerFailedRecordsDownload({
            columns,
            dispatch,
            title,
            failedRecords: processFailedRecords(parsedData.map(withAbortReason)),
            message
        });
        throw new Error(message);
    }
}

function closeImportForm(dispatch, namespace, relation) {
    dispatch({}, { verb: 'close', namespace, relation });
}

export const transformRowToObject =
    (columns, firstRowContainsHeader, log, foreignNamespace, foreignRelation) => {
        const normalizedColumns = normalizeImportColumns(columns);
        return (row, rowNum) => {
        if (firstRowContainsHeader && rowNum === 0) return undefined;
        if (!row.some(x => x?.trim && x.trim())) return undefined;
        const result = normalizedColumns.reduce((result, column, index) => {
            const def = column._meta;
            // don't do anything for skipped columns, or if there is no data for this column
            if (def.id === '_skip' || row.length <= index) return result;
            let key;
            try {
                const propertyName = getFieldPropertyName(def);
                if (def.isArrayElement) {
                    key = propertyName;
                    result[def.arrayProperty] = result[def.arrayProperty] || [];
                    result[def.arrayProperty][def.arrayIndex] = result[def.arrayProperty][def.arrayIndex] || {};
                    result[def.arrayProperty][def.arrayIndex][propertyName] = formatData(row[index], def);
                } else if (def.dataType && def.dataType.type === 'Lookup') {
                    key = `${getLookupNamespace(def)}:${getLookupRelation(def)}`;
                    result[key] = result[key] || {};
                    result[key][propertyName] = formatData(row[index], def);
                } else {
                    // Otherwise just assign the value
                    key = propertyName;
                    result[key] = formatData(row[index], def);
                }
            } catch (error) {
                const value = row[index];
                log.error(error);
                result.errors = result.errors || [];
                // if there is an error and the value fails validation, nothing is returned
                // so we need to add the value to the error object so the user can see what failed
                result.errors.push({
                    column: key,
                    message: error.message,
                    value
                });
            }

            return result;
        }, {});

        // Validate organization:person has either lastName or personId
        if (foreignNamespace === 'organization' && foreignRelation === 'person') {
            const { lastName, personId } = result;
            if (!lastName && !personId) {
                result.errors = result.errors || [];
                result.errors.push({
                    column: 'organization:person',
                    message: 'A Person must have either a Last Name or Person ID',
                    value: row
                });
                return result;
            }
        }

        return result;
        };
    };

const formatData = (value, field) => {
    // Handle required fields
    if (isFieldRequired(field) && !value && field.dataType?.type !== 'Boolean') {
        throw new Error(`Missing value for required field ${field.title}`);
    }

    // Trim string values
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    // Handle empty values for non-required fields
    // if it is not required, and nothing is there, don't complain.
    //
    // Integer and Boolean are excluded so a blank cell becomes 0 / false rather than vanishing,
    // which is right for a column where zero is a real answer. It is wrong for a quantity: a
    // blank Expected Qty means "not stated", and turning it into 0 both collides with a typed
    // zero — which has to be an error — and then fails `min`, so an empty cell REJECTED the row
    // instead of meaning one unit. `blankIsUnset` lets such a column opt out by name rather than
    // changing what a blank Integer means everywhere.
    const blankIsUnset = field.blankIsUnset === true;
    if (!trimmedValue && (blankIsUnset || !field.dataType || !['Boolean', 'Integer'].includes(field.dataType.type))) {
        return undefined;
    }

    // Check for scientific notation for sensitive fields (assetNo, serialNo, title)
    validateScientificNotation(trimmedValue, field);

    // Validate length constraints
    validateLengthConstraints(trimmedValue, field);

    // Handle fields without a specific data type
    if (!field.dataType || !field.dataType.type) {
        // no datatype => string, therefore, just return the data as string
        return trimmedValue?.toString();
    }

    // Handle Lookup fields, they should always be converted to strings
    if (field.dataType.type === 'Lookup') {
        return trimmedValue?.toString();
    }

    // Process specific data types
    switch (field.dataType.type) {
        case 'Hexadecimal':
            return validateHexadecimal(trimmedValue, field);
        case 'ASCII':
            return conversions.ascii.toHex(trimmedValue, true);
        case 'Currency':
            return processCurrency(trimmedValue, field);
        case 'Integer':
            return processInteger(trimmedValue, field);
        case 'Date/Time':
            return processDateTime(trimmedValue, field);
        case 'Email':
            return validateEmail(trimmedValue, field);
        case 'Boolean':
            return processBoolean(trimmedValue, field);
        default:
            return trimmedValue;
    }
};

// Helper functions to process and validate data types

const validateScientificNotation = (value, field) => {
    // Check for scientific notation for sensitive fields (assetNo, serialNo, title)
    const sensitiveFields = ['assetNo', 'serialNo', 'title'];
    if (
        typeof value === 'string' &&
        /^-?\d+(\.\d+)?[eE][+-]?\d{1,2}$/.test(value) &&
        sensitiveFields.includes(getFieldPropertyName(field))
    ) {
        throw new Error(`${field.title} contains a value in scientific notation, which is not allowed for this field`);
    }
};

const validateLengthConstraints = (value, field) => {
    if (field.minLength && value.length < field.minLength) {
        const add = field.minLength < field.maxLength ? 'or more ' : '';
        throw new Error(`${field.title} should have ${field.minLength} ${add}characters`);
    }
    if (field.maxLength && value.length > field.maxLength) {
        const add = field.minLength < field.maxLength ? 'or less ' : '';
        throw new Error(`${field.title} should have ${field.maxLength} ${add}characters`);
    }
};

const validateHexadecimal = (value, field) => {
    const isValid = /^[0-9A-Fa-f]*$/g.test(value);
    if (!isValid) {
        throw new Error(`${field.title} does not contain a valid HEX value`);
    }
    return value;
};

const processCurrency = (value, field) => {
    if (typeof value === 'undefined') return undefined;
    const replaceable = new RegExp(`${getThousandsSeparator(value)}|£|€`, 'g');
    // `$` doesn't play nice in regexp for some reason. just do it separately
    const valueWithoutLocaleFormatting = value.toString().replace(replaceable, '').replaceAll('$', '');
    const parsed = Number(valueWithoutLocaleFormatting);
    if (isNaN(parsed)) {
        throw new Error(`${field.title} does not contain a valid numeric value. `);
    }
    validateNumericRange(parsed, field);
    return parsed;
};

/** Text that spells out a whole number, with nothing but space around it. */
const WHOLE_NUMBER_TEXT = /^\s*\d+\s*$/;

const processInteger = (value, field) => {
    if (typeof value === 'undefined') return 0;
    // A column may insist the cell spells the number out.
    //
    // `Number()` is a parser for JavaScript source, not for a quantity: it reads '1e2' as 100,
    // '0x10' as 16 and '1.0' as 1. The server refuses all three, but it never saw them — this
    // ran first and handed on a clean integer, so the import succeeded with a number nobody
    // typed and the stricter rule on the other side could not fire. `strictDigits` is opt-in for
    // the same reason `blankIsUnset` is: other Integer columns have taken these spellings for
    // years and this is not the change that should stop them.
    if (field.strictDigits && typeof value === 'string' && !WHOLE_NUMBER_TEXT.test(value)) {
        throw new Error(`${field.title} must be a whole number, written in digits. `);
    }
    const parsed = Number(value);
    if (isNaN(parsed)) {
        throw new Error(`${field.title} does not contain a valid Integer value. `);
    }
    validateNumericRange(parsed, field);
    return parsed;
};

const validateNumericRange = (value, field) => {
    if (typeof field.min !== 'undefined' && value < field.min) {
        throw new Error(`${field.title} should be more than or equal to ${field.min}. `);
    }
    if (typeof field.max !== 'undefined' && value > field.max) {
        throw new Error(`${field.title} should be less than or equal to ${field.max}. `);
    }
};

const processDateTime = (value, field) => {
    try {
        if (isValidDate(new Date(value))) {
            return new Date(value);
        } else {
            throw new Error(`${field.title} does not contain a valid Date/Time value`);
        }
    } catch {
        throw new Error(`${field.title} does not contain a valid Date/Time value`);
    }
};

const validateEmail = (value, field) => {
    const isValid = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/.test(value);
    if (!isValid) {
        throw new Error(`${field.title} does not contain a valid Email value`);
    }
    return value;
};

const processBoolean = (value, field) => {
    const stringValue = value?.toString()?.trim();
    // if the value is undefined, null, or an empty string, return the default value
    // for example, if a user is importing an asset that does not have the 'Active' column and value in the import file,
    // but the 'Active' toggle has a default value of true, then the asset will be created as active.
    if ([undefined, null, ''].includes(stringValue)) {
        return field.defaultValue !== undefined ? field.defaultValue : undefined;
    }

    return !/^(false|no|0)$/i.test(stringValue);
};

const getThousandsSeparator = value => {
    // ideally, look at the string, see if it is #,###.## format
    // or the non-us #.###,## format
    const stringValue = value.toString();
    const nonDigits = stringValue.replace(/\d/g, '');
    if (nonDigits.includes(',.')) {
        return ',';
    } else if (nonDigits.includes('.,')) {
        return '.';
    }
    // otherwise assume US
    return ',';
};

function isFieldRequired(field) {
    return field?.['x-required'] ?? field?.required;
}

function getFieldPropertyName(field) {
    return field?.['x-propertyName'] ?? field?.propertyName;
}

function setFieldPropertyName(field, propertyName) {
    return {
        ...field,
        propertyName,
        ...(field?.['x-propertyName'] !== undefined ? { 'x-propertyName': propertyName } : {})
    };
}

function getLookupNamespace(field) {
    return field?.['x-namespace'] ?? field?.foreignNamespace;
}

function getLookupRelation(field) {
    return field?.['x-relation'] ?? field?.originalRelation ?? field?.foreignRelation;
}

function getCanonicalLookupPropertyName(field) {
    const namespace = getLookupNamespace(field);
    const relation = getLookupRelation(field);
    const foreignKey = namespace && relation ? `${namespace}:${relation}` : undefined;
    const inputDisplayProperty = field?.['x-inputDisplayProperties']?.[0] ?? field?.inputDisplayProperties?.[0];
    const propertyName = getFieldPropertyName(field);

    // Honor a non-self-keyed schema propertyName first — it's the field name
    // the rest of the system uses (server-side titleAlternative, grid display,
    // dependency unique-key resolution). Some lookup blocks list extra display
    // fields in inputDisplayProperties for UI hinting; preferring those over a
    // valid propertyName writes import data to the wrong inner field.
    if (propertyName && propertyName !== foreignKey) {
        return propertyName;
    }

    // Fall back to the block's display field when propertyName is missing or
    // self-keyed (the AB#9515 case — schema metadata exposing the foreign-key
    // path itself as x-propertyName).
    if (inputDisplayProperty) {
        return inputDisplayProperty;
    }

    return metadata.getTitleAlternative(namespace, relation, 'title');
}

function normalizeLookupField(field = {}) {
    if (field.dataType?.type !== 'Lookup') return field;

    const canonicalPropertyName = getCanonicalLookupPropertyName(field);
    return setFieldPropertyName(field, canonicalPropertyName);
}

function normalizeImportColumns(columns = []) {
    return columns.map(column =>
        column?._meta == null
            ? column
            : {
                  ...column,
                  _meta: normalizeLookupField(column._meta)
              }
    );
}
